import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { EMPTY, Observable, of } from 'rxjs';

import { environment } from 'src/environments/environment';
import { Perk, PerkVoucherCode, WidgetSize } from 'src/app/interfaces/perk';
import { EligiblePetsWrapper, Pet } from 'src/app/interfaces/pet';

import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PerkService {
  // MARK: - Properties

  petplanEligiblePets: Array<Pet> = [];

  // MARK: - Intialization

  constructor(private http: HttpClient) {}

  // MARK: - Public methods

  public getPerks = (): Observable<Perk[]> => {
    return this.getPerksFromAPI().pipe(
      map(
        (perkData) => this.replaceThePerkPosition(perkData.sort((a, b) => a.order - b.order)), // Sorting the array in ascending order by the 'order' field
      ),
      catchError((err) => {
        console.error(err);
        return of([]);
      }),
    );
  };

  public getPetplanEligiblePets(shortCode: string): Observable<EligiblePetsWrapper> {
    return this.http.get<EligiblePetsWrapper>(
      `${environment.apiUrl}/api/v2/perks/${shortCode}/user/me/pets`,
    );
  }

  public getPerkVoucerCode(shortCode: string): Observable<PerkVoucherCode> {
    return this.http.get<PerkVoucherCode>(
      `${environment.apiUrl}/api/v2/perks/${shortCode}/voucher`,
    );
  }

  public getPetEligibilityFromChipNumber(
    chipNumber: number,
    shortCode: string,
  ): Observable<{ petId: number; isEligible: boolean }> {
    // The eligibility endpoint only exists on brands that set a shortCode (petlog). On the others
    // it is empty, which would build a broken URL and return 404, so skip the call and emit nothing.
    // Subscribers keep their defaults (no petplan box shown), matching the previous behaviour.
    if (!shortCode) {
      return EMPTY;
    }
    return this.http.get<{ petId: number; isEligible: boolean }>(
      `${environment.apiUrl}/api/v2/perks/${shortCode}/pet/${chipNumber}/eligible`,
    );
  }

  // MARK: - Private methods

  private getPerksFromAPI(): Observable<Perk[]> {
    return this.http.get<Perk[]>(`${environment.apiUrl}/api/v2/perks/user/me`);
  }

  private perkNameExists(filteredPerks: Perk[], perkName: string): boolean {
    return filteredPerks.some((p) => p.name === perkName);
  }

  private replaceThePerkPosition(list: Array<Perk>): Array<Perk> {
    const isFirstPerkLarge = list[0].widget.widgetSize === WidgetSize.Large;
    const rowsToCount = 5;
    const correctingTheEffectOfTheLargePerk = 1;

    const petPlanTrialCheck =
      this.perkNameExists(list, 'petplan-trial-dog') ||
      this.perkNameExists(list, 'petplan-trial-cat');

    const largePerkSize = list.filter((perk) => perk.widget.widgetSize === WidgetSize.Large);
    const largePerkSizeCount = largePerkSize.length;

    const mediumPerkSize = list.filter((perk) => perk.widget.widgetSize === WidgetSize.Medium);
    const mediumPerkSizeCount = mediumPerkSize.length;
    const rowsWihtMediumSizePerk = Math.ceil(mediumPerkSizeCount / rowsToCount);
    const rowsWihtMediumSizePerkWithoutLargePerk = Math.floor(mediumPerkSizeCount / rowsToCount);
    const bottomLeftPerk =
      largePerkSizeCount !== 0
        ? rowsWihtMediumSizePerk * rowsToCount - correctingTheEffectOfTheLargePerk
        : rowsWihtMediumSizePerkWithoutLargePerk * rowsToCount;

    list.forEach((perk) => {
      let indexNumber = 0;

      const currentIndexOfObject = list.indexOf(perk);

      if (perk.filters.perkApparencePosition === 0) {
        indexNumber = isFirstPerkLarge ? 1 : 4;
      }

      if (perk.filters.perkApparencePosition === 1) {
        indexNumber = bottomLeftPerk - 1;
      }

      if (perk.filters.perkApparencePosition === 2) {
        indexNumber = petPlanTrialCheck ? (isFirstPerkLarge ? 1 : 4) : bottomLeftPerk;
      }

      if (perk.widget.widgetSize === WidgetSize.Small) {
        perk.widget.description = '';
      }

      // Check if the indexNumber is within the current array size
      if (indexNumber >= 0 && indexNumber <= list.length) {
        // Copy the list
        const newList = [...list];
        // Remove the object from the original position
        newList.splice(currentIndexOfObject, 1);
        // Insert it into the new position
        newList.splice(indexNumber, 0, perk);
        // Check if the removal and insertion were successful
        if (newList[indexNumber] === perk) {
          // Return the updated list
          list = newList;
        } else {
          console.error('An error occurred while moving the object.');
        }
      } else {
        console.error('The specified indexNumber is invalid.');
      }
    });
    // Return the original list if any error occurs
    return list.reverse();
  }
}
